import {
    Component,
    Element,
    Event,
    EventEmitter,
    h,
    Host,
    Prop,
} from '@stencil/core';
import {
    getApolloThemeFromTheme,
    ThemeType,
} from '@uipath/portal-shell-util';

import { Features } from '../../utils/featureFlags/featureFlagConstants';
import { getFeatureFlag } from '../../utils/featureFlags/featureFlags';
import { t } from '../../utils/localization/loc';
import Logger from '../../utils/logger/logger';
import Logo from './assets/logo.svg';
import LogoNoTm from './assets/logo-no-tm.svg';

/**
 * @slot slot-start - Use slot start to place content before feature and after the UiPath Logo, Eg : Status Chips
 * @slot slot-mid - Use slot mid to place content after feature in the middle of the app bar. Eg : Tab Navigation
 * @slot slot-end - Use slot end to place content before tenant picker at the end of the app bar; add a divider by using showSlotEndDivider
 */

@Component({
    tag: 'ap-app-bar',
    styleUrl: 'ap-app-bar.scss',
    shadow: true,
})
export class ApAppBar {
    @Element() $el: HTMLApAppBarElement;

    // #region Properties

    /**
     * [Required] Product name displayed next to the UiPath logo
     * @memberof ApAppBar
     */
    @Prop() product: string;

    /**
     * Title displayed to the right of the cobrand image,
     * eg: Tenant Management, Datasets, Services, etc.
     * @memberof ApAppBar
     */
    @Prop() feature: string = '';

    /**
     * Enable trademark for your product name
     * @memberof ApAppBar
     */
    @Prop() showProductTrademark: boolean = false;

    /**
     * Enable trademark for your product name
     * @memberof ApAppBar
     */
    @Prop() showLogoTrademark: boolean = true;

    /**
     * Show Co-brand image in header
     * @memberof ApAppBar
     */
    @Prop() cobrandingLogoUrl: string | null = null;

    /**
     * Provide Co-brand-label as alt tag
     * @memberof ApAppBar
     */
    @Prop() cobrandingLogoAlt: string | undefined = undefined;

    /**
     * Shows the preview tag on the right of the product name
     * @memberof ApAppBar
     */
    @Prop() isPreview: boolean = false;

    /**
     * Override for the tenant color hexcode.
     * Color will a hexcode. Can be uppercase or lowercase
     * @memberof ApAppBar
     */
    @Prop() tenantColor: string | null = null;

    /**
     * Show slot end divider if slot end is going to be populated with content
     * @memberof ApAppBar
     */
    @Prop() showSlotEndDivider = false;

    /**
     * Disables the product name from being clickable
     * @memberof ApAppBar
     */
    @Prop() disableProductClick: boolean = false;

    /**
     * Theme
     * @memberof ApAppBar
     */
    @Prop() theme: ThemeType = 'autoTheme';

    /**
     * Language
     * @memberof ApAppBar
     */
    @Prop() language: string = 'en';

    /**
     * Passes an href to the logo
     * @memberof ApAppBar
     */
    @Prop() logoClickHref?: string;

    /**
     * Passes an href to the feature name
     * @memberof ApAppBar
     */
    @Prop() featureNameClickHref?: string;

    // #endregion

    // #region Events

    /**
     * Triggered when the logo is clicked on the app bar.
     * The default event generated from the anchor tag is in the event.detail as event.detail.originalEvent.
     * If the originalEvent is not cancelled with preventDefault, the default link navigation will occur.
     */
    @Event({
        eventName: 'logoClicked',
        composed: true,
        cancelable: true,
        bubbles: true,
    }) onLogoClickedCustomEvent: EventEmitter;

    /**
     * Triggered when the feature name is clicked on the app bar.
     * The default event generated from the anchor tag is in the event.detail as event.detail.originalEvent.
     * If the originalEvent is not cancelled with preventDefault, the default link navigation will occur.
     */
    @Event({
        eventName: 'featureNameClicked',
        composed: true,
        cancelable: true,
        bubbles: true,
    }) onFeatureNameClickedCustomEvent: EventEmitter;

    // #endregion

    // #region Privates

    // #endregion

    // #region Listeners and Watchers

    // #endregion

    render() {
        const enableLeftNavRedesign = getFeatureFlag(Features.EnableLeftNavRedesign.name);
        Logger.debug([ 'ap-app-bar', 'render' ], 'rendering ap-app-bar');

        return (
            <Host class={getApolloThemeFromTheme(this.theme)}>
                <div
                    class={`pt-app-bar${enableLeftNavRedesign ? ' redesign' : ''}`}
                    data-cy="ap-app-bar"
                    style={this.getTenantColorStyle(this.tenantColor)}
                    role="navigation"
                    aria-label={t('app-bar-aria-label', this.language)}
                    data-test="ap-app-bar"
                >
                    <slot name="waffle-menu"/>
                    <div class="header">
                        { this.logoClickHref ? (
                            <a { ...this._getLogoAndProductProps() }>
                                {...this._getLogoAndProduct()}
                            </a>
                        ) : (
                            <button { ...this._getLogoAndProductProps() }>
                                {...this._getLogoAndProduct()}
                            </button>
                        )}

                        {
                            this.isPreview && (
                                <span class="preview-tag" data-test="preview-tag">
                                    {t('preview')}
                                </span>
                            )
                        }

                        {
                            this.cobrandingLogoUrl && (
                                <span class="co-brand" data-cy="co-brand">
                                    <portal-divider id="co-brand-divider" />
                                    <img
                                        id="co-brand-image"
                                        src={this.cobrandingLogoUrl}
                                        alt={this.cobrandingLogoAlt ?? t('default-cobranding-logo-alt-text', this.language)}
                                    />
                                </span>
                            )
                        }

                        <slot name="slot-start" />

                        {
                            this.feature && <portal-divider id="feature-divider" />
                        }
                        {
                            this.feature && (
                                this.featureNameClickHref ? (
                                    <a { ...this._getFeatureNameProps() }>
                                        {this.feature}
                                    </a>
                                ) : (
                                    <button { ...this._getFeatureNameProps() }>
                                        {this.feature}
                                    </button>
                                )
                            )
                        }

                        <slot name="slot-mid" />
                    </div>

                    <div class="slot-end">
                        <slot name="slot-end" />
                        {
                            this.showSlotEndDivider && <portal-divider id="slot-end-divider" marginSize='xxl' />
                        }

                        <slot name="help-menu" />
                        <slot name="notifications" />
                        <slot name="tenant-picker" />
                        <slot name="enterprise-trial" />
                        <slot name="profile-menu" />
                    </div>
                </div>
            </Host>
        );
    }

    private getTenantColorStyle(selectedTenantColor: string | null) {
        return selectedTenantColor ? { 'box-shadow': `0px 3px 0px ${selectedTenantColor} inset` } : undefined;
    }

    private onLogoClicked = (originalEvent: MouseEvent) => {
        Logger.info([ 'ap-app-bar', 'logo', 'click' ], 'clicked logo');
        this.onLogoClickedCustomEvent.emit({ originalEvent });
    };

    private onFeatureNameClicked = (originalEvent: MouseEvent) => {
        Logger.info([ 'ap-app-bar', 'featureName', 'click' ], 'clicked feature name');
        this.onFeatureNameClickedCustomEvent.emit({ originalEvent });
    };

    private _getLogoAndProductProps() {
        const sharedProps = {
            'aria-label': t('aria-label-logo', this.language, { serviceName: this.product }),
            'data-cy': 'product-logo',
            id: 'logo',
            class: 'logo',
            onClick: this.onLogoClicked,
        };

        if (this.logoClickHref) {
            return {
                ...sharedProps,
                href: this.logoClickHref,
            };
        }

        return {
            ...sharedProps,
            role: 'button',
            style: this.disableProductClick ? { 'cursor': 'default' } : { 'cursor': 'pointer' },
            disabled: this.disableProductClick,
        };
    }

    private _getLogoAndProduct() {
        return [
            <span
                key="logo-container"
                class={`logo-container ${this.product ? 'with-product' : ''}`}
                innerHTML={this.showLogoTrademark ? Logo : LogoNoTm}
            />,
            <span class="product" key="product">
                {this.product}
            </span>,
            ...(this.showProductTrademark ? [ <sup class="pt-trademark" key="product-trademark">TM</sup> ] : []),
        ];
    }

    private _getFeatureNameProps() {
        const sharedProps = {
            'aria-label': this.feature,
            'data-cy': 'feature',
            id: 'feature',
            class: 'feature',
            onClick: this.onFeatureNameClicked,
        };

        if (this.featureNameClickHref) {
            return {
                ...sharedProps,
                href: this.featureNameClickHref,
            };
        }

        return sharedProps;
    }
}
